/**
 * Searchable dashboard sections (reports/charts/tables/KPI blocks).
 *
 * Checklist when adding entries:
 * 1. dashboardId must exist in navigationMenu.ts (active menu item)
 * 2. Wrap component with DashboardSectionAnchor (sectionId must match)
 * 3. title must match the visible section heading in the UI
 */

export interface DashboardSectionSearchEntry {
  dashboardId: string;
  sectionId: string;
  title: string;
  keywords?: string[];
}

export const dashboardSectionIndex: DashboardSectionSearchEntry[] = [
  // ── main ──
  { dashboardId: 'main', sectionId: 'kpis', title: 'شاخص‌های کلیدی', keywords: ['KPI', 'شاخص'] },
  { dashboardId: 'main', sectionId: 'kpis', title: 'فروش کل', keywords: ['KPI', 'فروش'] },
  { dashboardId: 'main', sectionId: 'kpis', title: 'اشتراک‌گیری', keywords: ['KPI', 'subscription'] },
  { dashboardId: 'main', sectionId: 'kpis', title: 'رضایت مشتریان', keywords: ['KPI', 'رضایت'] },
  { dashboardId: 'main', sectionId: 'kpis', title: 'روزهای بدون شارژ پیامک', keywords: ['KPI', 'پیامک', 'SMS'] },
  {
    dashboardId: 'main',
    sectionId: 'customer-satisfaction-analysis',
    title: 'تحلیل رضایت مشتریان',
    keywords: ['رضایت', 'تحلیل'],
  },
  {
    dashboardId: 'main',
    sectionId: 'sms-charge-monthly-table',
    title: 'روزهای بدون شارژ پیامک به تفکیک ماه',
    keywords: ['پیامک', 'SMS', 'شارژ'],
  },
  {
    dashboardId: 'main',
    sectionId: 'customer-trends-balance-chart',
    title: 'تحلیل حفظ مشتریان',
    keywords: ['حفظ', 'وفاداری', 'مشتری'],
  },
  { dashboardId: 'main', sectionId: 'sales-trend', title: 'روند فروش', keywords: ['فروش', 'روند'] },

  // ── subscription ──
  {
    dashboardId: 'subscription',
    sectionId: 'overall-subscription',
    title: 'روند کلی اشتراک‌گیری',
    keywords: ['اشتراک', 'نمای کلی'],
  },
  {
    dashboardId: 'subscription',
    sectionId: 'shift-subscription',
    title: 'اشتراک‌گیری بر اساس شیفت کاری',
    keywords: ['اشتراک', 'شیفت'],
  },
  {
    dashboardId: 'subscription',
    sectionId: 'order-type-subscription',
    title: 'اشتراک‌گیری بر اساس نوع سفارش',
    keywords: ['اشتراک', 'نوع سفارش'],
  },

  // ── sales ──
  { dashboardId: 'sales', sectionId: 'sales-trend', title: 'روند فروش', keywords: ['فروش', 'روند'] },
  {
    dashboardId: 'sales',
    sectionId: 'product-category-sales-trend',
    title: 'روند فروش دسته‌بندی محصولات',
    keywords: ['دسته‌بندی', 'محصول', 'فروش', 'روند'],
  },
  {
    dashboardId: 'sales',
    sectionId: 'product-categorization',
    title: 'توزیع فروش دسته‌بندی محصولات (درصد)',
    keywords: ['دسته‌بندی', 'محصول', 'توزیع', 'درصد', 'pie'],
  },

  // ── market-pulse ──
  {
    dashboardId: 'market-pulse',
    sectionId: 'market-pulse-kpi',
    title: 'نتایج مقایسه کسب کار شما با کسب کار های متصل بازار',
    keywords: ['نبض', 'KPI', 'مقایسه', 'بازار'],
  },
  {
    dashboardId: 'market-pulse',
    sectionId: 'market-pulse-table',
    title: 'جدول مقایسه‌ی رشد ماهانه شعبه با کسب‌وکارهای متصل بازار',
    keywords: ['جدول', 'مقایسه', 'رشد'],
  },
  {
    dashboardId: 'market-pulse',
    sectionId: 'market-pulse-trend',
    title: 'روند مقایسه‌ای شعبه و کسب‌وکارهای متصل بازار',
    keywords: ['روند', 'نبض', 'بازار'],
  },
  {
    dashboardId: 'market-pulse',
    sectionId: 'market-pulse-yoy-table',
    title: 'مقایسه رشد شعبه با ماه متناظر سال قبل و کسب‌وکارهای متصل بازار',
    keywords: ['سال قبل', 'YoY', 'رشد'],
  },

  // ── brand-overview ──
  { dashboardId: 'brand-overview', sectionId: 'brand-kpis', title: 'شاخص‌های کلیدی برند', keywords: ['KPI', 'برند'] },
  { dashboardId: 'brand-overview', sectionId: 'brand-kpis', title: 'مجموع فروش', keywords: ['فروش', 'برند'] },
  { dashboardId: 'brand-overview', sectionId: 'brand-kpis', title: 'مجموع تعداد سفارش', keywords: ['سفارش', 'برند'] },
  { dashboardId: 'brand-overview', sectionId: 'brand-kpis', title: 'امتیاز رضایت', keywords: ['رضایت', 'برند'] },
  { dashboardId: 'brand-overview', sectionId: 'brand-kpis', title: 'اشتراک‌گیری', keywords: ['اشتراک', 'برند'] },
  {
    dashboardId: 'brand-overview',
    sectionId: 'brand-branch-comparison',
    title: 'مقایسه شعب با میانگین برند',
    keywords: ['مقایسه', 'شعب', 'برند'],
  },

  // ── evaluation-growth ──
  {
    dashboardId: 'evaluation-growth',
    sectionId: 'evaluation-growth-table',
    title: 'جدول عملکرد ماهانه ارزیابی',
    keywords: ['جدول', 'ارزیابی', 'رشد'],
  },

  // ── satisfaction ──
  {
    dashboardId: 'satisfaction',
    sectionId: 'satisfaction-summary',
    title: 'خلاصه رضایت',
    keywords: ['رضایت', 'KPI', 'خلاصه'],
  },
  { dashboardId: 'satisfaction', sectionId: 'satisfaction-summary', title: 'میانگین رضایت کلی', keywords: ['رضایت'] },
  { dashboardId: 'satisfaction', sectionId: 'satisfaction-summary', title: 'تعداد کل نظرسنجی‌ها', keywords: ['نظرسنجی'] },
  { dashboardId: 'satisfaction', sectionId: 'satisfaction-summary', title: 'بهترین شیفت کاری', keywords: ['شیفت'] },
  { dashboardId: 'satisfaction', sectionId: 'satisfaction-summary', title: 'بهترین نوع سفارش', keywords: ['نوع سفارش'] },
  {
    dashboardId: 'satisfaction',
    sectionId: 'overall-satisfaction',
    title: 'نمودار روند رضایت مشتریان',
    keywords: ['رضایت', 'روند', 'نمای کلی'],
  },
  {
    dashboardId: 'satisfaction',
    sectionId: 'shift-satisfaction',
    title: 'رضایت مشتریان بر اساس شیفت کاری',
    keywords: ['رضایت', 'شیفت'],
  },
  {
    dashboardId: 'satisfaction',
    sectionId: 'order-type-satisfaction',
    title: 'رضایت مشتریان بر اساس نوع سفارش',
    keywords: ['رضایت', 'نوع سفارش'],
  },
  {
    dashboardId: 'satisfaction',
    sectionId: 'detailed-satisfaction',
    title: 'شاخص‌های رضایت مشتریان به تفکیک دسته‌بندی',
    keywords: ['رضایت', 'دسته‌بندی'],
  },

  // ── refine-customer ──
  { dashboardId: 'refine-customer', sectionId: 'refine-kpis', title: 'شاخص‌های نظرسنجی', keywords: ['KPI', 'نظرسنجی'] },
  { dashboardId: 'refine-customer', sectionId: 'refine-kpis', title: 'فرم‌های نظرسنجی', keywords: ['نظرسنجی'] },
  { dashboardId: 'refine-customer', sectionId: 'refine-kpis', title: 'دلایل ثبت شده', keywords: ['دلایل'] },
  { dashboardId: 'refine-customer', sectionId: 'refine-kpis', title: 'درصد دلایل مثبت', keywords: ['مثبت'] },
  { dashboardId: 'refine-customer', sectionId: 'refine-kpis', title: 'بررسی شکایات', keywords: ['شکایت'] },
  {
    dashboardId: 'refine-customer',
    sectionId: 'survey-responses-by-answer',
    title: 'تعداد سوالات پاسخ داده شده به تفکیک نظر',
    keywords: ['نظرسنجی', 'پاسخ'],
  },
  {
    dashboardId: 'refine-customer',
    sectionId: 'complaint-review-status',
    title: 'وضعیت بررسی شکایات',
    keywords: ['شکایت', 'بررسی'],
  },
  {
    dashboardId: 'refine-customer',
    sectionId: 'dissatisfaction-reasons',
    title: 'گزارش بیشترین دلایل نارضایتی در یک ماه اخیر',
    keywords: ['نارضایتی', 'دلایل'],
  },
  {
    dashboardId: 'refine-customer',
    sectionId: 'dissatisfied-products',
    title: 'محصولات با بیشترین نارضایتی',
    keywords: ['محصول', 'نارضایتی'],
  },

  // ── hr ──
  { dashboardId: 'hr', sectionId: 'hr-kpis', title: 'شاخص‌های کلیدی عملکرد پرسنل', keywords: ['KPI', 'پرسنل'] },
  { dashboardId: 'hr', sectionId: 'hr-kpis', title: 'میانگین رضایت مشتریان', keywords: ['رضایت', 'پرسنل'] },
  { dashboardId: 'hr', sectionId: 'hr-kpis', title: 'کل پرسنل فعال', keywords: ['پرسنل'] },
  { dashboardId: 'hr', sectionId: 'hr-kpis', title: 'پرسنل عملکرد عالی', keywords: ['عملکرد'] },
  {
    dashboardId: 'hr',
    sectionId: 'personnel-performance-distribution',
    title: 'توزیع عملکرد پرسنل',
    keywords: ['پرسنل', 'عملکرد', 'توزیع'],
  },
  { dashboardId: 'hr', sectionId: 'team-composition', title: 'ترکیب تیم', keywords: ['تیم', 'پرسنل'] },
  { dashboardId: 'hr', sectionId: 'top-personnel-performance', title: 'عملکرد پرسنل', keywords: ['پرسنل', 'برتر'] },
  { dashboardId: 'hr', sectionId: 'key-performance-metrics', title: 'آمار کلیدی عملکرد', keywords: ['آمار', 'عملکرد'] },
  {
    dashboardId: 'hr',
    sectionId: 'comparison-performance-by-role',
    title: 'مقایسه رضایت بر اساس نقش',
    keywords: ['رضایت', 'نقش'],
  },
  {
    dashboardId: 'hr',
    sectionId: 'personnel-performance-by-role',
    title: 'عملکرد پرسنل به تفکیک نقش',
    keywords: ['پرسنل', 'نقش'],
  },

  // ── club ──
  {
    dashboardId: 'club',
    sectionId: 'club-parameters',
    title: 'پارامترهای باشگاه مشتریان',
    keywords: ['پارامتر', 'باشگاه'],
  },
  { dashboardId: 'club', sectionId: 'sales-parameters', title: 'پارامترهای فروش', keywords: ['فروش', 'پارامتر'] },

  // ── customer-purchase-behavior ──
  {
    dashboardId: 'customer-purchase-behavior',
    sectionId: 'customer-purchase-behavior-stacked',
    title: 'ترکیب گروه‌های خرید در هر ماه',
    keywords: ['خرید', 'گروه', 'ترکیب'],
  },
  {
    dashboardId: 'customer-purchase-behavior',
    sectionId: 'customer-purchase-behavior-group-growth',
    title: 'رشد هر گروه نسبت به ماه قبل',
    keywords: ['رشد', 'گروه'],
  },
  {
    dashboardId: 'customer-purchase-behavior',
    sectionId: 'customer-purchase-behavior-count-change-by-group',
    title: 'تغییر تعداد سفارش در هر گروه',
    keywords: ['تعداد', 'گروه', 'تغییر'],
  },
  {
    dashboardId: 'customer-purchase-behavior',
    sectionId: 'customer-purchase-behavior-group-count-by-month',
    title: 'روند ماهانهٔ یک گروه',
    keywords: ['روند', 'گروه'],
  },
  {
    dashboardId: 'customer-purchase-behavior',
    sectionId: 'customer-purchase-behavior-group-growth-returning-customers',
    title: 'رشد مشتریان جدید و بازگشتی',
    keywords: ['جدید', 'بازگشتی', 'رشد'],
  },

  // ── club-conditions ──
  {
    dashboardId: 'club-conditions',
    sectionId: 'club-conditions-table',
    title: 'گزارش عملکرد ماهانه شروط باشگاه مشتریان',
    keywords: ['شروط', 'جدول', 'باشگاه'],
  },
  {
    dashboardId: 'club-conditions',
    sectionId: 'generated-credit-ratio-chart',
    title: 'تعداد اعتبار تولیدی به تعداد سفارش',
    keywords: ['اعتبار', 'سفارش'],
  },
  {
    dashboardId: 'club-conditions',
    sectionId: 'generated-credit-amount-ratio-chart',
    title: 'مبلغ اعتبار تولیدی به مبلغ سفارش',
    keywords: ['اعتبار', 'مبلغ'],
  },
  {
    dashboardId: 'club-conditions',
    sectionId: 'participation-amount-chart',
    title: 'درصد مشارکت مبلغی شروط',
    keywords: ['مشارکت', 'مبلغ'],
  },
  {
    dashboardId: 'club-conditions',
    sectionId: 'participation-rate-chart',
    title: 'درصد مشارکت تعدادی شروط',
    keywords: ['مشارکت', 'تعدادی'],
  },
  {
    dashboardId: 'club-conditions',
    sectionId: 'used-credit-ratio-chart',
    title: 'میزان اعتبار مصرف شده به فروش',
    keywords: ['اعتبار', 'فروش'],
  },

  // ── customer-analytics ──
  {
    dashboardId: 'customer-analytics',
    sectionId: 'average-purchases-table',
    title: 'میانگین خرید مشتریان در ماه',
    keywords: ['میانگین', 'خرید'],
  },
  {
    dashboardId: 'customer-analytics',
    sectionId: 'average-purchases-yoy-table',
    title: 'میانگین خرید سالانه',
    keywords: ['میانگین', 'سالانه'],
  },
  {
    dashboardId: 'customer-analytics',
    sectionId: 'customer-report-section',
    title: 'گزارش تعداد مشتریانی که در یک سال گذشته خرید داشته یا نداشته‌اند',
    keywords: ['گزارش', 'مشتری'],
  },
  {
    dashboardId: 'customer-analytics',
    sectionId: 'customer-trends-balance-chart',
    title: 'تحلیل حفظ مشتریان',
    keywords: ['حفظ', 'روند', 'مشتری'],
  },
  {
    dashboardId: 'customer-analytics',
    sectionId: 'purchase-percentages-chart',
    title: 'درصد تعداد و مبلغ خرید مشتریان جدید و قدیمی',
    keywords: ['درصد', 'خرید', 'جدید', 'قدیمی'],
  },

  // ── customer-grouping ──
  {
    dashboardId: 'customer-grouping',
    sectionId: 'customer-grouping-summary',
    title: 'خلاصه گروه‌بندی مشتریان',
    keywords: ['خلاصه', 'مشتری'],
  },
  { dashboardId: 'customer-grouping', sectionId: 'customer-grouping-summary', title: 'مجموع مشتریان', keywords: ['مشتری'] },
  {
    dashboardId: 'customer-grouping',
    sectionId: 'customer-grouping-summary',
    title: 'مشتریان یک ماه اخیر',
    keywords: ['مشتری', 'ماه'],
  },
  {
    dashboardId: 'customer-grouping',
    sectionId: 'customer-grouping-summary',
    title: 'مشتریان یک سال اخیر',
    keywords: ['مشتری', 'سال'],
  },
  {
    dashboardId: 'customer-grouping',
    sectionId: 'customer-grouping-summary',
    title: 'مشتریان غیرفعال در یک سال گذشته',
    keywords: ['غیرفعال'],
  },
  {
    dashboardId: 'customer-grouping',
    sectionId: 'customer-grouping-purchase-count',
    title: 'گروه‌بندی بر اساس تعداد خرید',
    keywords: ['گروه‌بندی', 'تعداد خرید'],
  },
  {
    dashboardId: 'customer-grouping',
    sectionId: 'customer-grouping-distance-last-year',
    title: 'فاصله خرید کل مشتریان یک سال اخیر',
    keywords: ['فاصله', 'خرید', 'سال'],
  },
  {
    dashboardId: 'customer-grouping',
    sectionId: 'customer-grouping-distance-all',
    title: 'فاصله خرید کل مشتریان',
    keywords: ['فاصله', 'خرید'],
  },
  {
    dashboardId: 'customer-grouping',
    sectionId: 'customer-grouping-distance-comparison',
    title: 'مقایسه ریتم بازگشت: یک سال اخیر در برابر کل دوره',
    keywords: ['بازگشت', 'مقایسه', 'ریتم'],
  },
];
